import { create } from 'zustand';
import dayjs from 'dayjs';
import { clientPreferredDeliveryControllerApi } from '@api/api-config.ts';
import { PreferredDeliveryMode, PreferredDeliveryResponse } from '@api/generated/search-service';

interface PreferredDeliveryState {
  loading: boolean;
  saving: boolean;
  error: string | null;
  data: PreferredDeliveryResponse | undefined;

  // Calendar state
  currentMonth: dayjs.Dayjs;
  deliveryMode: PreferredDeliveryMode;
  selectedDate: Date;
  blockedDates: Date[];

  // Actions
  loadPreferredDelivery: (portalId: number, organizationId: number) => Promise<void>;
  savePreferredDelivery: (portalId: number, organizationId: number) => Promise<void>;
  setCurrentMonth: (month: dayjs.Dayjs) => void;
  setDeliveryMode: (mode: PreferredDeliveryMode) => void;
  setSelectedDate: (date: Date) => void;
  setBlockedDates: (dates: Date[]) => void;
  goToPreviousMonth: () => void;
  goToNextMonth: () => void;

  // Helpers
  getMinDate: () => dayjs.Dayjs;
  getDaysInMonth: () => (dayjs.Dayjs | null)[];
  isDateBlocked: (date: dayjs.Dayjs) => boolean;
  isDateDisabled: (date: dayjs.Dayjs) => boolean;
  isDateSelected: (date: dayjs.Dayjs) => boolean;
}

export const useCreatePreferredDelivery = create<PreferredDeliveryState>((set, get) => ({
  loading: false,
  saving: false,
  error: null,
  data: undefined,

  // Calendar state
  currentMonth: dayjs(),
  deliveryMode: PreferredDeliveryMode.NoEarlierThan,
  selectedDate: new Date(),
  blockedDates: [],

  loadPreferredDelivery: async (portalId, organizationId) => {
    try {
      set({ loading: true, error: null });

      const res = await clientPreferredDeliveryControllerApi.getOrCreatePreferredDelivery({ organizationId, portalId });

      const deliveryDate = res.data.deliveryDate ? new Date(res.data.deliveryDate) : new Date();

      set({
        loading: false,
        data: res.data,
        selectedDate: deliveryDate,
        currentMonth: dayjs(deliveryDate),
        deliveryMode: res.data.mode || PreferredDeliveryMode.NoEarlierThan,
      });
    } catch (e: any) {
      const errorMessage = e.response?.data?.message || e.message || 'Nepodařilo se načíst preferované dodání';
      set({ loading: false, error: errorMessage });
    }
  },

  savePreferredDelivery: async (portalId, organizationId) => {
    try {
      set({ saving: true, error: null });

      const { selectedDate, deliveryMode } = get();

      await clientPreferredDeliveryControllerApi.setPreferredDelivery({
        organizationId,
        portalId,
        setPreferredDeliveryRequest: {
          preferredDate: dayjs(selectedDate).format('YYYY-MM-DD'),
          mode: deliveryMode,
        },
      });

      set({ saving: false, selectedDate, deliveryMode });
    } catch (e: any) {
      const errorMessage = e.response?.data?.message || e.message || 'Nepodařilo se uložit preferované dodání';
      set({ saving: false, error: errorMessage });
    }
  },

  setCurrentMonth: month => set({ currentMonth: month }),

  setDeliveryMode: mode => set({ deliveryMode: mode }),

  setSelectedDate: date => set({ selectedDate: date, currentMonth: dayjs(date) }),

  setBlockedDates: dates => set({ blockedDates: dates }),

  goToPreviousMonth: () => set(state => ({ currentMonth: state.currentMonth.subtract(1, 'month') })),

  goToNextMonth: () => set(state => ({ currentMonth: state.currentMonth.add(1, 'month') })),

  getMinDate: () => dayjs().add(1, 'day').startOf('day'),

  getDaysInMonth: () => {
    const { currentMonth } = get();
    const startOfMonth = currentMonth.startOf('month');
    const endOfMonth = currentMonth.endOf('month');
    const startDay = startOfMonth.day();
    const daysCount = endOfMonth.date();

    const days: (dayjs.Dayjs | null)[] = [];

    const firstDayOfWeek = startDay === 0 ? 6 : startDay - 1;
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysCount; day++) {
      days.push(startOfMonth.date(day));
    }

    return days;
  },

  isDateBlocked: date => {
    const { blockedDates } = get();
    return blockedDates.some(blockedDate => dayjs(blockedDate).isSame(date, 'day'));
  },

  isDateDisabled: date => {
    const minDate = get().getMinDate();
    return date.isBefore(minDate, 'day');
  },

  isDateSelected: date => {
    const { selectedDate } = get();
    return dayjs(selectedDate).isSame(date, 'day');
  },
}));
